import { useState } from "react";
import { Link } from "react-router-dom";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { ArrowLeft, Mail, Loader2, CheckCircle } from "lucide-react";
import axios from "axios";
import { toast } from "sonner";
import { API } from "../App";
import EditableText from "../components/EditableText";

const ForgotPasswordPage = () => {
  const [email, setEmail] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!email.trim()) return;
    setSubmitting(true);
    try {
      await axios.post(`${API}/auth/forgot-password`, { email: email.trim() });
      setSubmitted(true);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Couldn't send reset link. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#FAF7F2] flex items-center justify-center px-4 py-12">
      <div className="w-full max-w-md">
        <Link
          to="/login"
          className="inline-flex items-center gap-2 text-sm text-[#64748B] hover:text-[#6B4E8A] mb-6 transition-colors"
          data-testid="back-to-login"
        >
          <ArrowLeft className="w-4 h-4" />
          Back to sign in
        </Link>

        <div className="bg-white rounded-3xl border border-[#E5DFEC] shadow-sm p-8">
          <div className="flex justify-center mb-6">
            <img src="/dhdh-logo.png" alt="DHDH" className="w-16 h-16 rounded-full object-cover" />
          </div>

          {!submitted ? (
            <>
              <EditableText
                blockKey="forgot.title"
                fallback="Reset your password"
                as="h1"
                className="text-2xl font-medium text-[#2A2A2A] text-center mb-2"
                style={{ fontFamily: "Outfit, sans-serif" }}
              />
              <EditableText
                blockKey="forgot.subtitle"
                fallback="Enter the email tied to your account and we'll send you a link to choose a new password."
                as="p"
                className="text-sm text-[#64748B] text-center mb-8 leading-relaxed"
                multiline
              />

              <form onSubmit={handleSubmit} className="space-y-4" data-testid="forgot-password-form">
                <div>
                  <Label htmlFor="email" className="text-sm text-[#475569]">Email</Label>
                  <Input
                    id="email"
                    type="email"
                    required
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    placeholder="you@example.com"
                    disabled={submitting}
                    className="mt-1.5 bg-[#FAF7F2] border-[#E5DFEC] rounded-xl"
                    data-testid="forgot-password-email-input"
                  />
                </div>
                <Button
                  type="submit"
                  disabled={submitting || !email.trim()}
                  className="w-full bg-[#6B4E8A] text-white hover:bg-[#553D6F] disabled:bg-[#94A3B8] rounded-full py-6 font-medium"
                  data-testid="forgot-password-submit"
                >
                  {submitting ? (
                    <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Sending…</>
                  ) : (
                    "Send reset link"
                  )}
                </Button>
              </form>
            </>
          ) : (
            <div className="text-center" data-testid="forgot-password-success">
              <div className="w-14 h-14 rounded-full bg-[#10B981]/10 flex items-center justify-center mx-auto mb-4">
                <Mail className="w-7 h-7 text-[#10B981]" />
              </div>
              <h2
                className="text-xl font-medium text-[#2A2A2A] mb-2"
                style={{ fontFamily: "Outfit, sans-serif" }}
              >
                Check your inbox
              </h2>
              <p className="text-sm text-[#64748B] leading-relaxed">
                If an account with <span className="font-medium text-[#2A2A2A]">{email}</span> exists, we've sent a password reset link. The link will expire in 1 hour.
              </p>
              <p className="text-xs text-[#94A3B8] mt-4">
                Didn't get it? Check your spam folder, or{" "}
                <button
                  type="button"
                  onClick={() => setSubmitted(false)}
                  className="text-[#6B4E8A] hover:underline font-medium"
                  data-testid="forgot-password-retry"
                >
                  try again
                </button>
                .
              </p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default ForgotPasswordPage;
